import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../components/index.js";
import { toast, type BnBadgeVariant, type BnColumn, type BnRow } from "../components/index.js";
import { navigate } from "../nav.js";
import { detailedError, humanError } from "../api/errors.js";
import { getRepository } from "../api/repositories.js";
import { listTeams, roleAtLeast, type Role } from "../api/teams.js";
import {
  getDeployment,
  getEdition,
  listEditions,
  downloadEditionFile,
  rebuildEdition,
  type Deployment,
  type Edition,
  type EditionSummary,
} from "../api/deployments.js";

/** chainLimit bounds how many corrected editions the page follows back. */
const chainLimit = 20;

function formatTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function statusVariant(status: string): BnBadgeVariant {
  return status === "delivered" ? "success" : status === "blocked" ? "error" : "info";
}

/**
 * page-edition (/repositories/:id/deployments/:deploymentId/editions/:editionId)
 * is one edition of a deployment: where it came from (commit, seals, image,
 * parameters, trigger), its files with their digests, the corrections it
 * belongs to, and for a maintainer a rebuild, which makes it again from what
 * it records so its outputs digest can be compared.
 */
@customElement("page-edition")
export class PageEdition extends LitElement {
  @property() repositoryId = "";
  @property() deploymentId = "";
  @property() editionId = "";

  @state() private dep: Deployment | null = null;
  @state() private edition: Edition | null = null;
  /** The editions this one corrects, newest first. */
  @state() private corrected: Edition[] = [];
  /** The editions that correct this one. */
  @state() private corrections: EditionSummary[] = [];
  @state() private teamRole: Role = "viewer";
  @state() private loading = true;
  @state() private error = "";
  @state() private rebuilding = false;
  @state() private rebuildId = "";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-flex;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      margin-bottom: var(--bino-space-md);
    }
    .head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-lg);
      flex-wrap: wrap;
    }
    h1 {
      margin: 0 var(--bino-space-sm) 0 0;
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
    }
    .spacer {
      flex: 1;
    }
    .sections {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-lg);
    }
    .meta {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
      gap: var(--bino-space-md);
    }
    .meta .k {
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bino-text-secondary);
      margin-bottom: var(--bino-space-xs);
    }
    .meta .v {
      font-size: var(--bino-font-size-md);
      word-break: break-all;
    }
    .mono {
      font-family: var(--bino-font-mono);
    }
    .hint {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      margin: 0;
    }
    .link-btn {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font-size: var(--bino-font-size-base);
      color: var(--bino-primary);
      text-decoration: underline;
    }
    ul {
      margin: 0;
      padding-left: 1.25rem;
    }
  `;

  override updated(changed: PropertyValues) {
    if (changed.has("editionId") && this.editionId) void this.load();
  }

  private get depBase(): string {
    return `/repositories/${encodeURIComponent(this.repositoryId)}/deployments/${encodeURIComponent(this.deploymentId)}`;
  }

  private async load() {
    const id = this.editionId;
    const stale = () => !this.isConnected || id !== this.editionId;
    this.loading = true;
    this.error = "";
    this.rebuildId = "";
    try {
      const [dep, edition, repo, teams] = await Promise.all([
        getDeployment(this.repositoryId, this.deploymentId),
        getEdition(this.repositoryId, this.deploymentId, id),
        getRepository(this.repositoryId),
        listTeams().catch(() => []),
      ]);
      if (stale()) return;
      this.dep = dep;
      this.edition = edition;
      this.teamRole = teams.find((t) => t.id === repo.teamId)?.role ?? "viewer";
    } catch (e) {
      if (stale()) return;
      this.error = humanError(e);
      this.loading = false;
      return;
    }
    this.loading = false;
    void this.loadChain(id);
  }

  /**
   * loadChain follows the corrections back from this edition, and finds the
   * editions on the first page of the list that correct it.
   */
  private async loadChain(id: string) {
    try {
      const corrected: Edition[] = [];
      let next = this.edition?.correctionOf;
      while (next && corrected.length < chainLimit) {
        const ed = await getEdition(this.repositoryId, this.deploymentId, next);
        corrected.push(ed);
        next = ed.correctionOf;
      }
      const page = await listEditions(this.repositoryId, this.deploymentId);
      if (id !== this.editionId) return;
      this.corrected = corrected;
      this.corrections = page.items.filter((ed) => ed.correctionOf === id);
    } catch (e) {
      toast(humanError(e), "error");
    }
  }

  private async download(name: string) {
    try {
      const blob = await downloadEditionFile(
        this.repositoryId,
        this.deploymentId,
        this.editionId,
        name,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name.split("/").pop() ?? name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast(humanError(e), "error");
    }
  }

  private async rebuild() {
    this.rebuilding = true;
    try {
      this.rebuildId = (
        await rebuildEdition(this.repositoryId, this.deploymentId, this.editionId)
      ).buildId;
      toast("Rebuild queued", "success");
    } catch (e) {
      toast(detailedError(e), "error");
    } finally {
      this.rebuilding = false;
    }
  }

  private open(ed: EditionSummary) {
    navigate(`${this.depBase}/editions/${encodeURIComponent(ed.id)}`);
  }

  override render() {
    if (this.loading && !this.edition) {
      return html`<bn-spinner block label="Loading edition…"></bn-spinner>`;
    }
    if (this.error || !this.edition || !this.dep) {
      return html`<bn-empty-state
        icon="⚠"
        heading="Couldn’t load the edition"
        message=${this.error || "Edition not found."}
      >
        <bn-button slot="actions" variant="secondary" @click=${() => navigate(this.depBase)}
          >Back to deployment</bn-button
        >
      </bn-empty-state>`;
    }
    const ed = this.edition;
    return html`
      <a
        class="back"
        href=${this.depBase}
        @click=${(e: Event) => {
          e.preventDefault();
          navigate(this.depBase);
        }}
        >← ${this.dep.artefact}</a
      >
      <div class="head">
        <h1>Edition ${ed.seq} · ${ed.period}</h1>
        <bn-badge .variant=${statusVariant(ed.status)}>${ed.status}</bn-badge>
        ${ed.correctionOf ? html`<bn-badge variant="info">correction</bn-badge>` : nothing}
        <span class="spacer"></span>
        ${roleAtLeast(this.teamRole, "maintainer")
          ? html`<bn-button
              size="sm"
              variant="secondary"
              ?loading=${this.rebuilding}
              @click=${() => this.rebuild()}
              >Rebuild</bn-button
            >`
          : nothing}
      </div>
      <div class="sections">
        ${this.rebuildId
          ? html`<bn-card heading="Rebuild">
              <p class="hint">
                The rebuild runs this edition's commit with the same seals and parameters. It does
                not create an edition. Download the files of
                <button class="link-btn" @click=${() => navigate(`/builds/${this.rebuildId}`)}>
                  its build</button
                >
                and compare their SHA-256 with the files below.
              </p>
            </bn-card>`
          : nothing}
        ${this.renderProvenance(ed)} ${this.renderFiles(ed)} ${this.renderChain()}
      </div>
    `;
  }

  private renderProvenance(ed: Edition) {
    const v = (k: string, value: unknown, mono = true) =>
      html`<div>
        <div class="k">${k}</div>
        <div class="v ${mono ? "mono" : ""}">${value || "—"}</div>
      </div>`;
    return html`<bn-card heading="Provenance">
      <div class="meta">
        ${v("Commit", ed.commit)} ${v("Period", ed.period)} ${v("Data until", ed.dataUntil)}
        ${v("Trigger", ed.trigger, false)} ${v("Seal set", ed.sealSetDigest)}
        ${v("Image", ed.image)} ${v("Image digest", ed.imageDigest)}
        ${v("Lock digest", ed.lockDigest)} ${v("Outputs digest", ed.outputsDigest)}
        ${v("Made", formatTime(ed.created), false)}
        ${v("Delivered", formatTime(ed.deliveredAt), false)}
        <div>
          <div class="k">Build</div>
          <div class="v">
            ${ed.buildId
              ? html`<button class="link-btn" @click=${() => navigate(`/builds/${ed.buildId}`)}>
                  ${ed.buildId}
                </button>`
              : "—"}
          </div>
        </div>
      </div>
      <p class="hint" style="margin-top:var(--bino-space-md)">Seals</p>
      ${ed.seals.length
        ? html`<ul>
            ${ed.seals.map((s) => html`<li class="mono">${s.month} · ${s.sealId}</li>`)}
          </ul>`
        : html`<p class="hint">None.</p>`}
      ${ed.params && Object.keys(ed.params).length
        ? html`<p class="hint" style="margin-top:var(--bino-space-md)">Parameters</p>
            <ul>
              ${Object.entries(ed.params).map(
                ([k, val]) => html`<li class="mono">${k}=${val}</li>`,
              )}
            </ul>`
        : nothing}
    </bn-card>`;
  }

  private renderFiles(ed: Edition) {
    const removed = ed.filesRemovedAt;
    return html`<bn-card heading="Files" flush>
      ${removed
        ? html`<p class="hint" style="padding:var(--bino-space-md)">
            The files were removed ${formatTime(removed)}: an edition that is never delivered keeps
            them 30 days after it was made. The list shows what they were.
          </p>`
        : nothing}
      <bn-table
        .columns=${[
          { key: "name", label: "Name", mono: true },
          { key: "size", label: "Size", align: "right", mono: true, width: "1px" },
          { key: "sha", label: "SHA-256", mono: true },
          { key: "actions", label: "", align: "right", width: "1px" },
        ] as BnColumn[]}
        .rows=${ed.files.map(
          (f): BnRow => ({
            name: f.name,
            size: formatBytes(f.size),
            sha: html`<span title=${f.sha256}>${f.sha256.slice(0, 16)}</span>`,
            actions: removed
              ? nothing
              : html`<bn-button
                  size="sm"
                  variant="secondary"
                  @click=${() => this.download(f.name)}
                  >Download</bn-button
                >`,
          }),
        )}
        empty="This edition has no files."
      ></bn-table>
    </bn-card>`;
  }

  private renderChain() {
    if (this.corrected.length === 0 && this.corrections.length === 0) return nothing;
    const link = (ed: EditionSummary) =>
      html`<li>
        <button class="link-btn" @click=${() => this.open(ed)}>Edition ${ed.seq}</button>
        · ${ed.status} · ${formatTime(ed.created)}
      </li>`;
    return html`<bn-card heading="Corrections">
      ${this.corrected.length
        ? html`<p class="hint">This edition corrects:</p>
            <ul>
              ${this.corrected.map(link)}
            </ul>`
        : nothing}
      ${this.corrections.length
        ? html`<p class="hint">Corrected by:</p>
            <ul>
              ${this.corrections.map(link)}
            </ul>`
        : nothing}
    </bn-card>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-edition": PageEdition;
  }
}
